import { jitsiLocalStorage } from '@jitsi/js-utils';
import i18next from 'i18next';
import { sha256 } from 'js-sha256';
import _ from 'lodash';
import moment from 'moment';
import { PureComponent } from 'react';

import {
    createLiveStreamingDialogEvent,
    sendAnalytics
} from '../../../analytics';
import logger from '../../../base/app/logger';
import { apiKEY } from '../../../base/config/constants';
import { MHRecordingConstants } from '../../../base/lib-meet-hour';
import {
    IS_RECORDING_AND_LIVESTREAMING_ON,
    recordingandLivestreamingOn
} from '../../../base/mtApi';
import { ULR_PATHS } from '../../../base/mtApi/constants';
import { fetchAxiosApi } from '../../../base/mtApi/functions';
import {
    checkIfNotReactNative,
    isDomainWeborNative
} from '../../../base/util/checkOS';
import {
    getDropboxData,
    getNewAccessToken as getNewDropboxAccessToken,
    isEnabled as isDropboxEnabled,
    updateDropboxToken
} from '../../../dropbox';
import { showErrorNotification } from '../../../notifications';
import {
    getOneDriveData,
    isEnabled as isOneDriveEnabled,
    updateOneDriveToken
} from '../../../onedrive';
import { toggleRequestingSubtitles } from '../../../subtitles/actions';
import {
    RECORDING_TYPES,
    isCustomS3RecordingStorage,
    isLocalRecordingStorage
} from '../../constants';
import { startRecordingSafely } from '../../functions';
import {
    markProviderValidationFailed,
    markProviderValidationSuccess,
    resetProviderSavedTokens,
    resetProviderValidation,
    tryMarkProviderTokenSaved,
    tryStartProviderValidation
} from '../providerRequestDedupe';

/**
 * The type of the React {@code Component} props of
 * {@link AbstractStartLiveStreamDialog}.
 */
export type Props = {

    /**
     * The {@code MHConference} for the current conference.
     */
    _conference: Object,

    /**
     * The current state of interactions with the Google API. Determines what
     * Google related UI should display.
     */
    _googleAPIState: number,

    /**
     * The email of the user currently logged in to the Google web client
     * application.
     */
    _googleProfileEmail: string,

    /**
     * The live stream key that was used before.
     */
    _streamKey: string,

    /**
     * The app key for the dropbox authentication.
     */
    _appKey: string,

    /**
     * Whether to show file recordings service, even if integrations
     * are enabled.
     */
    _fileRecordingsServiceEnabled: boolean,

    /**
     * Whether to show the possibility to share file recording with other people (e.g. Meeting participants),
     *  based on the actual implementation on the backend.
     */
    _fileRecordingsServiceSharingEnabled: boolean,

    /**
     * If true the dropbox integration is enabled, otherwise - disabled.
     */
    _isDropboxEnabled: boolean,

    /**
     * If true the OneDrive integration is enabled, otherwise - disabled.
     */
    _isOneDriveEnabled: boolean,

    /**
     * The dropbox access token.
     */
    token: ?string,

    /**
     * The OneDrive access token.
     */
    _oneDriveToken: ?string,

    /**
     */
    _token: ?string,
    _userId: ?Object,

    /**
     * The Redux dispatch function.
     */
    dispatch: Function,

    /**
     * The dropbox refresh token.
     */
    _rToken: string,

    /**
     * The OneDrive refresh token.
     */
    _oneDriveRToken: string,

    /**
     * Access token's expiration date as UNIX timestamp.
     */
    _tokenExpireDate?: number,

    /**
     * OneDrive access token expiration date.
     */
    _oneDriveTokenExpireDate?: number,

    /**
     * OneDrive client id.
     */
    _oneDriveClientId: string,

    /**
     * OneDrive OAuth strategy.
     */
    _oneDriveAuthStrategy: string,

    /**
     * Backend endpoint used for server-side OneDrive token exchange.
     */
    _oneDriveServerTokenEndpoint: string,

    /**
     * Whether OneDrive sign-in popup + token exchange is in progress.
     */
    _oneDriveAuthorizing: boolean,

    /**
     * Recording & Live Streaming ON & OFF State.
     */
    _isRecordingandLivestreamingOn?: Boolean,

    /**
     * Invoked to obtain translated strings.
     */
    t: Function,

    _liveStream: ?Array<Object>,

    _recording: ?Array<Object>,
    _roomName: ?string,
};

/**
 * The type of the React {@code Component} state of
 * {@link AbstractStartLiveStreamDialog}.
 */
export type State = {

    /**
     * Details about the broadcasts available for use for the logged in Google
     * user's YouTube account.
     */
    broadcasts: ?Array<Object>,

    /**
     * The error type, as provided by Google, for the most recent error
     * encountered by the Google API.
     */
    errorType: ?string,

    /**
     * The boundStreamID of the broadcast currently selected in the broadcast
     * dropdown.
     */
    selectedBoundStreamID: ?string,

    /**
     * <tt>true</tt> if we have valid oauth token.
     */
    isTokenValid: boolean,

    /**
     * <tt>true</tt> if we are in process of validating the oauth token.
     */
    isValidating: boolean,

    /**
     * The currently selected recording service of type: RECORDING_TYPES.
     */
    selectedRecordingService: ?string,

    /**
     * True if the user requested the service to share the recording with others.
     */
    sharingEnabled: boolean,

    /**
     * Number of MiB of available space in user's Dropbox account.
     */
    spaceLeft: ?number,

    /**
     * Number of MiB of available space in user's OneDrive account.
     */
    oneDriveSpaceLeft: ?number,

    /**
     * The display name of the user's OneDrive account.
     */
    oneDriveUserName: ?string,

    /**
     * The owner email of the user's OneDrive account.
     */
    oneDriveUserEmail: ?string,

    /**
     * Number of MiB of total OneDrive storage quota.
     */
    oneDriveSpaceTotal: ?number,

    /**
     * Number of MiB of used OneDrive storage quota.
     */
    oneDriveSpaceUsed: ?number,

    /**
     * The display name of the user's Dropbox account.
     */
    userName: ?string,

    /**
     * If recording is also enabled or not.
     */
    recordingEnabled: boolean,

    /**
     * Record with DropBox.
     */
    recStreamEnabled: boolean,

    /**
     * Display the Dialog Box.
     */
    dialogOpen: boolean,

    /**
     * LiveStream data from the interface_config.
     */
    liveStreamTools: ?Array<Object>,

    /**
     * Recording Data from the interface_config.
     */
    recTools: ?Array<Object>,

    /**
     * StreamKeys could be anything from facebook to youtube.
     */
    streamKeys: Object,

    /**
     * Recording is enabled or not.
     */
    recordingO: ?boolean,

    /**
     * Streaming keys that are enabled or not.
     */
    streamEnabledMobile: Object,

    /**
     * Steaming enalbe in the browser environment.
     */
    streamsEnabled: Object,

    /**
     * APi url version.
     */
    apiN: Boolean,

    /**
     * Acces Token.
     */
    _accessToken: ?string,
};

const DEFAULT_RTMPS_STATE = {
    Vimeo: 'rtmps://rtmp-global.cloud.vimeo.com:443/live',
    'Custom RTMPS': '', // Custom RTMPS
    'Custom RTMP': '', // Custom RTMPS
    Instagram: 'rtmps://live-upload.instagram.com:443/rtmp/', // Instagram RTMPS
    LinkedIn:
        'rtmps://12c7101ae60e40ca9597078681ee5fc3.channel.media.azure.net:2935/live/', // LinkedIn RTMP
    Twitch: 'rtmp://jfk.contribute.live-video.net/app/', // Twitch RTMP
    Youtube: 'rtmp://a.rtmp.youtube.com/live2/', // Youtube RTMP
    Facebook: 'rtmps://live-api-s.facebook.com:443/rtmp/' // Facebook RTMPS
};

/**
 * Parses storage values that may come as number or string.
 *
 * @param {number|string|null|void} value - Storage value.
 * @returns {?number}
 */
function parseStorageValue(value: number | string | null | void) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string') {
        const parsed = Number.parseFloat(value.replace(/,/g, '').trim());

        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
}

/**
 * Implements an abstract class for the StartLiveStreamDialog on both platforms.
 *
 * NOTE: Google log-in is not supported for mobile yet for later implementation
 * but the abstraction of its properties are already present in this abstract
 * class.
 */
export default class AbstractStartLiveStreamDialog<P: Props> extends PureComponent<
    P,
    State
> {
    _isMounted: boolean;
    _dropboxTokenFromRecordSettings: ?string;
    _oneDriveTokenFromRecordSettings: ?string;
    _lastTokenValidationKey: ?string;
    _inFlightTokenValidationKey: ?string;
    _lastSavedDropboxToken: ?string;
    _lastSavedOneDriveToken: ?string;

    /**
   * Constructor of the component.
   *
   * @inheritdoc
   */
    constructor(props: P) {
        super(props);

        this._dropboxTokenFromRecordSettings = null;
        this._oneDriveTokenFromRecordSettings = null;
        this._lastTokenValidationKey = null;
        this._inFlightTokenValidationKey = null;
        this._lastSavedDropboxToken = null;
        this._lastSavedOneDriveToken = null;

        let selectedRecordingService;

        if (
            this.props._fileRecordingsServiceEnabled
            || !this._areIntegrationsEnabled()
        ) {
            selectedRecordingService = RECORDING_TYPES.MH_REC_SERVICE;
        } else if (this.props._isOneDriveEnabled) {
            selectedRecordingService = RECORDING_TYPES.ONEDRIVE;
        } else if (this._areIntegrationsEnabled()) {
            selectedRecordingService = RECORDING_TYPES.DROPBOX;
        }

        this.state = {
            broadcasts: undefined,
            errorType: undefined,
            selectedBoundStreamID: undefined,
            streamEnabledMobile:
                typeof jitsiLocalStorage.getItem('streamEnabledMobile') === 'string'
                    ? JSON.parse(jitsiLocalStorage.getItem('streamEnabledMobile'))
                    : [],
            streamEnabledWeb:
                typeof jitsiLocalStorage.getItem('streamEnabledWeb') === 'string'
                    ? JSON.parse(jitsiLocalStorage.getItem('streamEnabledWeb'))
                    : [],
            streamsEnabled:
                JSON.parse(jitsiLocalStorage.getItem('liveStreamDetails'))
                    ?.streamsEnable ?? {},
            isTokenValid: false,
            isValidating: false,
            userName: undefined,
            openDialog: [],
            openDialog0: false,
            openDialog1: false,
            openDialog2: false,
            streamKeys: JSON.parse(jitsiLocalStorage.getItem('liveStreamDetails'))
                ?.streamKeys ?? {
                streamKey1: null,
                streamKey2: null,
                streamKey3: null
            },
            closeDialog: false,
            recordingO:
                JSON.parse(jitsiLocalStorage.getItem('liveStreamDetails'))?.recording
                || false,
            sharingEnabled: true,
            spaceLeft: undefined,
            oneDriveSpaceLeft: undefined,
            oneDriveUserName: undefined,
            oneDriveUserEmail: undefined,
            oneDriveSpaceTotal: undefined,
            oneDriveSpaceUsed: undefined,
            selectedRecordingService,
            recordingEnabled: false,
            recStreamEnabled: false,
            dialogOpen: false,
            liveStreamTools: props._liveStream,
            vap: false,
            recTools: props._recording,
            datatitle: null,
            recTypeData: {},
            isAws: false,
            isMobile: false,
            isLoaded: true,
            dropLoaded: false
        };

        /**
     * Instance variable used to flag whether the component is or is not
     * mounted. Used as a hack to avoid setting state on an unmounted
     * component.
     *
     * @private
     * @type {boolean}
     */
        // this._isMounted = false;

        this._onCancel = this._onCancel.bind(this);
        this._onDisable = this._onDisable.bind(this);
        this._onSelectedRecordingServiceChanged
            = this._onSelectedRecordingServiceChanged.bind(this);
        this._onSharingSettingChanged = this._onSharingSettingChanged.bind(this);
        this._onSubmit = this._onSubmit.bind(this);
        this._onSubmitNative = this._onSubmitNative.bind(this);
    }

    /**
   * Implements {@link Component#componentDidMount()}. Invoked immediately
   * after this component is mounted.
   *
   * @inheritdoc
   * @returns {void}
   */
    // eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
    async componentDidMount() {
        // this._isMounted = true;
        this._fetchRecordingConfig();
    }

    /**
     * Fetches recording configuration from the API.
     * Can be called from componentDidMount and when recording toggle is re-enabled.
     *
     * @returns {void}
     */
    _fetchRecordingConfig() {
        const {
            apiN: { API_URL_PREFIX, API_VERSION_PREFIX, MH_CLIENT_ID }
        } = this.props;
        const moement = moment().seconds(0)
            .milliseconds(0)
            .format('X');

        const mesh256 = sha256(`${apiKEY}:MH:${moement}`);

        fetchAxiosApi(ULR_PATHS.AUTO_REC_STREAM, {
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            body: {
                client_id: MH_CLIENT_ID,
                meeting_id: this.props._roomName,
                credentials: mesh256,
                ...isDomainWeborNative
            },
            config: {
                API_URL_PREFIX,
                API_VERSION_PREFIX
            }
        })
            .then(redordingResponse => {
                const fallbackRecordingService = this.props._isDropboxEnabled
                    ? RECORDING_TYPES.DROPBOX
                    : this.props._isOneDriveEnabled
                        ? RECORDING_TYPES.ONEDRIVE
                        : this.state.selectedRecordingService;

                if (!redordingResponse || redordingResponse instanceof Error) {
                    this.setState(prevState => {
                        return {
                            ...prevState,
                            selectedRecordingService: fallbackRecordingService,
                            isTokenValid: false,
                            isValidating: false,
                            isLoaded: false
                        };
                    });

                    return;
                }
                if (redordingResponse?.success === false) {
                    const isDropboxAuthMissing = redordingResponse?.code === 1033;
                    const isOneDriveAuthMissing = redordingResponse?.code === 1429;
                    const isMeetHourStorageFull = redordingResponse?.code === 1032;
                    const hasLocalDropboxToken = Boolean(this.props.token);
                    const hasLocalOneDriveToken = Boolean(this.props._oneDriveToken);
                    const canFallbackToLocalDropbox = isDropboxAuthMissing && hasLocalDropboxToken;
                    const canFallbackToLocalOneDrive = isOneDriveAuthMissing && hasLocalOneDriveToken;
                    const selectedServiceFromLocalFallback = canFallbackToLocalOneDrive
                        ? RECORDING_TYPES.ONEDRIVE
                        : canFallbackToLocalDropbox
                            ? RECORDING_TYPES.DROPBOX
                            : isMeetHourStorageFull
                                ? RECORDING_TYPES.MH_REC_SERVICE
                                : isDropboxAuthMissing
                                    ? RECORDING_TYPES.DROPBOX
                                    : isOneDriveAuthMissing
                                        ? RECORDING_TYPES.ONEDRIVE
                                        : fallbackRecordingService;
                    const shouldUseLocalFallback = canFallbackToLocalDropbox || canFallbackToLocalOneDrive;

                    this.setState(prevState => {
                        return {
                            ...prevState,
                            dropLoaded: true,
                            isTokenValid: false,
                            isValidating: shouldUseLocalFallback,
                            userName: isDropboxAuthMissing ? undefined : prevState.userName,
                            spaceLeft: isDropboxAuthMissing ? undefined : prevState.spaceLeft,
                            oneDriveUserName: isOneDriveAuthMissing ? undefined : prevState.oneDriveUserName,
                            oneDriveSpaceLeft: isOneDriveAuthMissing ? undefined : prevState.oneDriveSpaceLeft,
                            oneDriveUserEmail: isOneDriveAuthMissing ? undefined : prevState.oneDriveUserEmail,
                            oneDriveSpaceTotal: isOneDriveAuthMissing ? undefined : prevState.oneDriveSpaceTotal,
                            oneDriveSpaceUsed: isOneDriveAuthMissing ? undefined : prevState.oneDriveSpaceUsed,
                            recTypeData: {
                                ...prevState.recTypeData,
                                storage: isMeetHourStorageFull
                                    ? prevState.recTypeData?.storage || 'local'
                                    : prevState.recTypeData?.storage,
                                s3_free_space: redordingResponse?.s3_free_space
                                    ?? prevState.recTypeData?.s3_free_space
                                    ?? null,
                                s3_used_space: redordingResponse?.s3_used_space
                                    ?? prevState.recTypeData?.s3_used_space
                                    ?? null,
                                code: redordingResponse?.code
                            },
                            isAws: isMeetHourStorageFull ? true : prevState.isAws,
                            selectedRecordingService: selectedServiceFromLocalFallback,
                            isLoaded: false
                        };
                    }, () => {
                        // API is the source of truth, but if it returns no token,
                        // keep valid local auth and hydrate signed-in provider details.
                        if (shouldUseLocalFallback) {
                            this._onTokenUpdated();
                        }
                    });
                }
                if (redordingResponse?.success === true) {
                    const recData = redordingResponse;
                    const settings = recData?.settings || {};
                    const storageType = recData?.default_recording_storage;
                    const dropboxTokenSource = settings?.dropbox_access_token;
                    const oneDriveTokenSource = settings?.onedrive_access_token
                        || (storageType === 'OneDrive' ? settings : undefined);
                    const dropboxToken = dropboxTokenSource?.access_token;
                    const dropboxRToken = dropboxTokenSource?.refresh_token;
                    const dropboxExpires = dropboxTokenSource?.expires || dropboxTokenSource?.expires_in;
                    const oneDriveToken = oneDriveTokenSource?.access_token;
                    const oneDriveRToken = oneDriveTokenSource?.refresh_token;
                    const oneDriveExpires = oneDriveTokenSource?.expires || oneDriveTokenSource?.expires_in;
                    const selectedRecordingService = isCustomS3RecordingStorage(storageType)
                        ? RECORDING_TYPES.CUSTOM_S3
                        : isLocalRecordingStorage(storageType)
                            ? RECORDING_TYPES.MH_REC_SERVICE
                            : storageType === 'OneDrive'
                                ? RECORDING_TYPES.ONEDRIVE
                                : storageType === 'Dropbox'
                                    ? RECORDING_TYPES.DROPBOX
                                    : this.state.selectedRecordingService;

                    // Hydrate both providers from portal settings when available.
                    if (dropboxToken) {
                        this._dropboxTokenFromRecordSettings = dropboxToken;
                        this.props.dispatch(updateDropboxToken(dropboxToken, dropboxRToken, dropboxExpires));
                    }
                    if (oneDriveToken) {
                        this._oneDriveTokenFromRecordSettings = oneDriveToken;
                        this.props.dispatch(updateOneDriveToken(oneDriveToken, oneDriveRToken, oneDriveExpires));
                    }
                    this.setState(prevState => {
                        return {
                            ...prevState,
                            recTypeData: {
                                settings: recData.settings,
                                storage: storageType,
                                s3_free_space: recData?.s3_free_space
                                    ? recData?.s3_free_space
                                    : null,
                                s3_used_space: recData?.s3_used_space
                                    ? recData?.s3_used_space
                                    : null
                            },
                            selectedRecordingService,
                            dropLoaded: true,
                            isTokenValid: Boolean(dropboxToken || oneDriveToken),
                            isValidating: Boolean(dropboxToken || oneDriveToken),
                            isAws: Boolean(
                                recData?.default_recording_storage !== 'Dropbox'
                                && recData?.default_recording_storage !== 'OneDrive'
                                && !_.isNil(recData?.default_recording_storage)
                            ),
                            isLoaded: false
                        };
                    }, () => {
                        // Always call _onTokenUpdated from the setState callback so the
                        // correct selectedRecordingService is visible regardless of
                        // whether the Redux dispatch and setState batched together.
                        this._onTokenUpdated();
                    });
                }
            })
            .catch(es => {
                const fallbackRecordingService = this.props._isDropboxEnabled
                    ? RECORDING_TYPES.DROPBOX
                    : this.props._isOneDriveEnabled
                        ? RECORDING_TYPES.ONEDRIVE
                        : this.state.selectedRecordingService;

                this.setState(prevState => {
                    return {
                        ...prevState,
                        selectedRecordingService: fallbackRecordingService,
                        isTokenValid: false,
                        isValidating: false,
                        isLoaded: false
                    };
                });
                logger.error('Error in fetching prejoin api For guest', es);
            });
    }

    /**
   * Implements React's {@link Component#componentWillUnmount()}. Invoked
   * immediately before this component is unmounted and destroyed.
   *
   * @inheritdoc
   */
    componentWillUnmount() {
        // this._isMounted = false;
    }

    /**
   * Validates the oauth access token.
   *
   * @inheritdoc
   * @returns {void}
   */
    componentDidUpdate(prevProps: Props) {
        const oneDriveAuthFinished = prevProps._oneDriveAuthorizing && !this.props._oneDriveAuthorizing;
        const oneDriveAuthStarted = !prevProps._oneDriveAuthorizing && this.props._oneDriveAuthorizing;

        // Show spinner immediately when Sign In is clicked.
        if (oneDriveAuthStarted) {
            this.setState({ isValidating: true,
                isTokenValid: false });
        }

        // Refresh config only when auth finished without a token (fallback)
        // or when a brand-new token appears from non-recordsettings flows.
        const shouldReloadConfigAfterAuth = oneDriveAuthFinished && !this.props._oneDriveToken;

        if (shouldReloadConfigAfterAuth) {
            this._fetchRecordingConfig();
        }

        // Clear cached recordsettings tokens when user explicitly signs out.
        if (prevProps._oneDriveToken && !this.props._oneDriveToken) {
            this._oneDriveTokenFromRecordSettings = null;
            this._lastSavedOneDriveToken = null;
            this._lastTokenValidationKey = null;
            resetProviderValidation(`${RECORDING_TYPES.ONEDRIVE}:`);
            resetProviderSavedTokens(`${RECORDING_TYPES.ONEDRIVE}:`);
        }

        if (prevProps.token && !this.props.token) {
            this._dropboxTokenFromRecordSettings = null;
            this._lastSavedDropboxToken = null;
            this._lastTokenValidationKey = null;
            resetProviderValidation(`${RECORDING_TYPES.DROPBOX}:`);
            resetProviderSavedTokens(`${RECORDING_TYPES.DROPBOX}:`);
        }

        if (this.props.token !== prevProps.token
            || this.props._oneDriveToken !== prevProps._oneDriveToken
            || this.props._accessToken !== prevProps._accessToken
            || oneDriveAuthFinished) {
            this._onTokenUpdated();
        }
    }

    _areIntegrationsEnabled: () => boolean;

    /**
   * Returns true if the integrations with third party services are enabled
   * and false otherwise.
   *
   * @returns {boolean} - True if the integrations with third party services
   * are enabled and false otherwise.
   */
    _areIntegrationsEnabled() {
        return this.props._isDropboxEnabled || this.props._isOneDriveEnabled;
    }

    _onSharingSettingChanged: () => void;

    _isStorageFullForSelectedRecordingService: () => boolean;

    /**
   * Returns whether selected recording storage is full.
   *
   * @returns {boolean}
   */
    _isStorageFullForSelectedRecordingService() {
        const { selectedRecordingService, oneDriveSpaceLeft, spaceLeft, recTypeData, isAws } = this.state;
        const storageType = recTypeData?.storage;

        if (recTypeData?.code === 1032) {
            return true;
        }

        if (selectedRecordingService === RECORDING_TYPES.ONEDRIVE) {
            const oneDriveFreeSpace = parseStorageValue(oneDriveSpaceLeft);

            return oneDriveFreeSpace !== null && oneDriveFreeSpace <= 0;
        }

        if (selectedRecordingService === RECORDING_TYPES.DROPBOX) {
            const dropboxFreeSpace = parseStorageValue(spaceLeft);

            return dropboxFreeSpace !== null && dropboxFreeSpace <= 0;
        }

        if (isCustomS3RecordingStorage(storageType)) {
            const s3FreeSpace = parseStorageValue(recTypeData?.s3_free_space);

            return s3FreeSpace !== null && s3FreeSpace <= 0;
        }

        if (isAws || selectedRecordingService === RECORDING_TYPES.MH_REC_SERVICE) {
            const s3FreeSpace = parseStorageValue(recTypeData?.s3_free_space);

            return s3FreeSpace !== null && s3FreeSpace <= 0;
        }

        return false;
    }

    /**
   * Callback to handle sharing setting change from the dialog.
   *
   * @returns {void}
   */
    _onSharingSettingChanged() {
        this.setState({
            sharingEnabled: !this.state.sharingEnabled
        });
    }

    _onSelectedRecordingServiceChanged: (string) => void;

    /**
   * Handles selected recording service changes.
   *
   * @param {string} selectedRecordingService - The new selected recording
   * service.
   * @returns {void}
   */
    _onSelectedRecordingServiceChanged(selectedRecordingService) {
        this.setState({ selectedRecordingService }, () => {
            this._onTokenUpdated();
        });
    }

    /**
   * Validates the dropbox access token and fetches account information.
   *
   * @returns {void}
   */
    _onTokenUpdated() {
        const {
            _appKey,
            _isDropboxEnabled,
            _isOneDriveEnabled,
            token,
            _oneDriveToken,
            _accessToken,
            _tokenExpireDate,
            _rToken,
            _oneDriveTokenExpireDate,
            _oneDriveRToken,
            _oneDriveClientId,
            dispatch
        } = this.props;
        const resolvedDropboxToken = token || this._dropboxTokenFromRecordSettings;
        const resolvedOneDriveToken = _oneDriveToken || this._oneDriveTokenFromRecordSettings;

        if (!_isDropboxEnabled && !_isOneDriveEnabled) {
            return;
        }

        // While OneDrive popup + server exchange is in flight, show spinner immediately.
        if (this.props._oneDriveAuthorizing) {
            this.setState({
                isTokenValid: false,
                isValidating: true
            });

            return;
        }

        if (!resolvedDropboxToken && !resolvedOneDriveToken) {
            this.setState({
                isTokenValid: false,
                isValidating: false
            });
        } else {
            const selectedRecordingService = this.state.selectedRecordingService;
            const isDropboxService = selectedRecordingService === RECORDING_TYPES.DROPBOX;
            const isOneDriveService = selectedRecordingService === RECORDING_TYPES.ONEDRIVE;

            // For non-integration services (MH_REC_SERVICE, Custom S3, etc.) no token
            // validation is needed — do not call provider Graph APIs for these services.
            if (!isDropboxService && !isOneDriveService) {
                return;
            }

            // If the selected provider's token is absent (signed out or server returned none),
            // show sign-in button immediately.
            const providerToken = isOneDriveService ? resolvedOneDriveToken : resolvedDropboxToken;

            if (!providerToken) {
                this.setState({ isTokenValid: false,
                    isValidating: false });

                return;
            }

            const validationKey = `${selectedRecordingService}:${providerToken}`;

            if (!tryStartProviderValidation(validationKey)) {
                return;
            }

            this._inFlightTokenValidationKey = validationKey;

            const shouldRefreshDropboxToken = isDropboxService
                && Boolean(resolvedDropboxToken)
                && _tokenExpireDate
                && Date.now() > new Date(_tokenExpireDate)
                && checkIfNotReactNative() === true;

            if (shouldRefreshDropboxToken) {
                getNewDropboxAccessToken(_appKey, _rToken)
                    .then(resp =>
                        dispatch(updateDropboxToken(resp.token, resp.rToken, resp.expireDate))
                    )
                    .catch(error => {
                        logger.log('Cannot refresh Dropbox token', error);
                        dispatch(showErrorNotification({
                            titleKey: 'recording.dropboxError',
                            descriptionKey: 'recording.dropboxRefreshTokenFailed'
                        }));
                        this.setState({
                            isTokenValid: false,
                            isValidating: false
                        });
                    });

                return;
            }

            if (this.state.dropLoaded && _accessToken) {
                const {
                    apiN: { API_URL_PREFIX, API_VERSION_PREFIX }
                } = this.props;
                const shouldSaveDropboxToken = isDropboxService
                    && Boolean(resolvedDropboxToken)
                    && resolvedDropboxToken !== this._dropboxTokenFromRecordSettings
                    && resolvedDropboxToken !== this._lastSavedDropboxToken
                    && tryMarkProviderTokenSaved(`${RECORDING_TYPES.DROPBOX}:${resolvedDropboxToken}`);
                const shouldSaveOneDriveToken = isOneDriveService
                    && Boolean(resolvedOneDriveToken)
                    && Boolean(_oneDriveRToken)
                    && resolvedOneDriveToken !== this._oneDriveTokenFromRecordSettings
                    && resolvedOneDriveToken !== this._lastSavedOneDriveToken
                    && tryMarkProviderTokenSaved(`${RECORDING_TYPES.ONEDRIVE}:${resolvedOneDriveToken}`);

                if (shouldSaveDropboxToken) {
                    this._lastSavedDropboxToken = resolvedDropboxToken;
                    fetchAxiosApi('customer/savedropboxtoken', {
                        method: 'post',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${_accessToken}`
                        },
                        body: {
                            meeting_id: this.props._roomName,
                            dropbox_token: resolvedDropboxToken,
                            refresh_token: _rToken,
                            expires: `${_tokenExpireDate}`
                        },
                        config: {
                            API_URL_PREFIX,
                            API_VERSION_PREFIX
                        }
                    });
                }

                if (shouldSaveOneDriveToken) {
                    this._lastSavedOneDriveToken = resolvedOneDriveToken;
                    fetchAxiosApi('customer/saveonedrivetoken', {
                        method: 'post',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${_accessToken}`
                        },
                        body: {
                            meeting_id: this.props._roomName,
                            onedrive_token: resolvedOneDriveToken,
                            refresh_token: _oneDriveRToken,
                            expires_in: `${_oneDriveTokenExpireDate}`
                        },
                        config: {
                            API_URL_PREFIX,
                            API_VERSION_PREFIX
                        }
                    });
                }
                this.setState({
                    isTokenValid: false,
                    isValidating: true
                });
            }

            const shouldFetchDropboxData = isDropboxService && Boolean(resolvedDropboxToken);
            const shouldFetchOneDriveData = isOneDriveService && Boolean(resolvedOneDriveToken);

            if (!shouldFetchDropboxData && !shouldFetchOneDriveData) {
                return;
            }

            this.setState({
                isTokenValid: false,
                isValidating: true
            });

            if (shouldFetchDropboxData) {
                getDropboxData(resolvedDropboxToken, _appKey).then(data => {
                    this._inFlightTokenValidationKey = null;
                    this._lastTokenValidationKey = validationKey;
                    markProviderValidationSuccess(validationKey);
                    if (typeof data === 'undefined') {
                        this.setState({
                            isTokenValid: true,
                            isValidating: false
                        });
                    } else {
                        this.setState({
                            isTokenValid: true,
                            isValidating: false,
                            ...data
                        });
                    }
                })
                .catch(error => {
                    this._inFlightTokenValidationKey = null;
                    markProviderValidationFailed(validationKey);
                    logger.log('getDropboxDataError', error);
                    this.setState({ isTokenValid: false,
                        isValidating: false });
                });
            }

            if (shouldFetchOneDriveData) {
                getOneDriveData(resolvedOneDriveToken, _oneDriveClientId, {
                    rToken: _oneDriveRToken,
                    strategy: this.props._oneDriveAuthStrategy,
                    onRefreshed: refreshed => {
                        dispatch(updateOneDriveToken(refreshed.token, refreshed.rToken, refreshed.expireDate));
                    }
                }).then(data => {
                    this._inFlightTokenValidationKey = null;
                    this._lastTokenValidationKey = validationKey;
                    markProviderValidationSuccess(validationKey);
                    if (typeof data === 'undefined') {
                        this.setState({
                            isTokenValid: true,
                            isValidating: false
                        });
                    } else {
                        this.setState({
                            isTokenValid: true,
                            isValidating: false,
                            oneDriveUserName: data.userName,
                            oneDriveSpaceLeft: data.spaceLeft,
                            oneDriveUserEmail: data.userEmail,
                            oneDriveSpaceTotal: data.spaceTotalMB,
                            oneDriveSpaceUsed: data.spaceUsedMB
                        });
                    }
                })
                .catch(error => {
                    this._inFlightTokenValidationKey = null;
                    markProviderValidationFailed(validationKey);
                    logger.log('getOneDriveDataError', error);
                    this.setState({ isTokenValid: false,
                        isValidating: false });
                });
            }
        }
    }
    _onCancel: () => boolean;

    /**
   * Invokes the passed in {@link onCancel} callback and closes
   * {@code StartLiveStreamDialog}.
   *
   * @private
   * @returns {boolean} True is returned to close the modal.
   */
    _onCancel() {
        sendAnalytics(createLiveStreamingDialogEvent('start', 'cancel.button'));

        return true;
    }

    _onDisable: () => Boolean;

    /**
   * Invokes the passed in {@link _onDisable} callback and closes
   * {@code StartLiveStreamDialog}.
   *
   * @param {boolean} webValue - WEb.
   * @private
   * @returns {boolean} True is returned to close the modal.
   */
    _onDisable(webValue = false) {
        if (webValue) {
            // Check if we have any valid stream configurations
            if (
                !Array.isArray(this.state.streamEnabledWeb)
                || this.state.streamEnabledWeb.length === 0
            ) {
                return true; // Disable if no streams configured
            }

            // Check if at least one stream has a valid value or is a recording
            const hasValidStream = this.state.streamEnabledWeb.some(stream => {
                // Recording streams don't need a value
                if (stream?.isRec === true) {
                    return true;
                }


                // Live streams need a valid value
                return (
                    stream?.value
                    && typeof stream.value === 'string'
                    && stream.value.trim() !== ''
                );
            });

            const hasRecordingStream = this.state.streamEnabledWeb.some(stream => stream?.isRec === true);

            if (hasRecordingStream && this._isStorageFullForSelectedRecordingService()) {
                return true;
            }

            return !hasValidStream; // Disable if no valid streams
        }

        const keyState = this.state.streamKeys;

        // Check if we have any valid stream keys or recording is enabled
        const hasValidKeys
            = (keyState.streamKey1 && keyState.streamKey1.trim() !== '')
            || (keyState.streamKey2 && keyState.streamKey2.trim() !== '')
            || (keyState.streamKey3 && keyState.streamKey3.trim() !== '')
            || this.state.recordingO;

        if (this.state.recordingO && this._isStorageFullForSelectedRecordingService()) {
            return true;
        }

        return !hasValidKeys;
    }

    /**
   * Asks the user to sign in, if not already signed in, and then requests a
   * list of the user's YouTube broadcasts.
   *
   * NOTE: To be implemented by platforms.
   *
   * @private
   * @returns {Promise}
   */
    _onGetYouTubeBroadcasts: () => Promise<*>;

    _onSubmit: () => boolean;

    /**
   * Invokes the passed in {@link onSubmit} callback with the entered stream
   * key, and then closes {@code StartLiveStreamDialog}.
   *
   * @private
   * @returns {boolean} False if no stream key is entered to preventing
   * closing, true to close the modal.
   */
    _onSubmit() {
        const { broadcasts, selectedBoundStreamID } = this.state;
        const {
            token,
            _oneDriveToken,
            apiN: { API_URL_PREFIX, API_VERSION_PREFIX, MH_CLIENT_ID },
            _accessToken,
            _roomName,
            _autoCaptionOnRecord,
            _confSettings,
            _oneDriveRToken,
            _oneDriveClientId,
            _rToken
        } = this.props;
        const storageType = this.state.recTypeData?.storage;
        const isLegacyCustomStorage = Boolean(storageType)
            && !isLocalRecordingStorage(storageType)
            && storageType !== 'Dropbox'
            && storageType !== 'OneDrive';
        const isCustomS3Recording = isCustomS3RecordingStorage(storageType)
            || isLegacyCustomStorage;
        const hasRecordingRequested = Array.isArray(this.state.streamEnabledWeb)
            && this.state.streamEnabledWeb.some(stream => stream?.isRec === true);

        if (hasRecordingRequested && this._isStorageFullForSelectedRecordingService()) {
            this.props.dispatch(showErrorNotification({
                titleKey: 'recording.storageFullTitle',
                descriptionKey: 'recording.storageFullDescription'
            }));

            return false;
        }

        let appData;
        const attributes = {};
        let selectedBroadcastID = null;

        if (selectedBoundStreamID) {
            const selectedBroadcast
                = broadcasts
                && broadcasts.find(
                    broadcast => broadcast.boundStreamID === selectedBoundStreamID
                );

            selectedBroadcastID = selectedBroadcast && selectedBroadcast.id;
        }

        const streamKeys = { ...this.state.streamKeys };

        const streamsEnable = { ...this.state.streamsEnabled };
        const streamWeb = JSON.stringify(this.state.streamEnabledWeb);
        const streams = {
            streamKeys,

            // streamEnable,
            streamsEnable,
            recording: this.state.recordingO
        };
        const storageAssign = JSON.stringify(streams);

        jitsiLocalStorage.setItem('streamEnabledWeb', streamWeb);
        jitsiLocalStorage.setItem('liveStreamDetails', storageAssign);
        sendAnalytics(createLiveStreamingDialogEvent('start', 'confirm.button'));

        const isDropboxRecording = this.state.selectedRecordingService === RECORDING_TYPES.DROPBOX && token;
        const isOneDriveRecording = this.state.selectedRecordingService === RECORDING_TYPES.ONEDRIVE && _oneDriveToken;
        const isMeetHourStorage = this.state.selectedRecordingService === RECORDING_TYPES.MH_REC_SERVICE;
        const isCloudRecording = isDropboxRecording || isOneDriveRecording || isCustomS3Recording || isMeetHourStorage;

        if (isCloudRecording) {
            const isAwsLikeRecording = isCustomS3Recording || isMeetHourStorage;
            const recordingType = isAwsLikeRecording
                ? storageType
                : isOneDriveRecording
                    ? 'OneDrive'
                    : 'Dropbox';
            const serviceData = isAwsLikeRecording
                ? this.state.recTypeData
                : {
                    token: isOneDriveRecording ? _oneDriveToken : token
                };
            const recordURL = `${API_URL_PREFIX}${API_VERSION_PREFIX}customer/saverecordings`;
            const decryptUrl = `${API_URL_PREFIX}${API_VERSION_PREFIX}customer/recordsettings_decrypt`;

            appData = JSON.stringify({
                file_recording_metadata: {
                    upload_credentials: {
                        // Keep legacy wire value so downstream Jibri/portal parsing remains compatible.
                        service_name: isAwsLikeRecording
                            ? 'awss3'
                            : isOneDriveRecording
                                ? RECORDING_TYPES.ONEDRIVE
                                : RECORDING_TYPES.DROPBOX,
                        recordingType,
                        ...serviceData,
                        userid: this.props._userId?.id,
                        _accessToken,
                        client_id: MH_CLIENT_ID,
                        onedrive_client_id: _oneDriveClientId,
                        r_token: isOneDriveRecording ? _oneDriveRToken : _rToken,
                        api_key: apiKEY,
                        decryptUrl,
                        ...isDomainWeborNative,
                        urlSaved: recordURL,
                        startTiming: new Date().getTime(),

                        meetingid: _roomName
                    }
                }
            });
            attributes.type = isCustomS3Recording
                ? RECORDING_TYPES.CUSTOM_S3
                : isMeetHourStorage
                    ? RECORDING_TYPES.MH_REC_SERVICE
                    : isOneDriveRecording
                        ? RECORDING_TYPES.ONEDRIVE
                        : RECORDING_TYPES.DROPBOX;
        } else {
            appData = JSON.stringify({
                file_recording_metadata: {
                    share: this.state.sharingEnabled
                }
            });
            attributes.type = RECORDING_TYPES.MH_REC_SERVICE;
        }

        const Mode = this.state.recordingO
            ? MHRecordingConstants.mode.FILE
            : MHRecordingConstants.mode.STREAM;

        // const stateKeys = this.state.streamKeys;
        const stateEnabled = this.state.streamEnabledWeb;

        const streamEntries = Array.isArray(stateEnabled)
            ? stateEnabled
                .map(itm => {
                    let rtmpUrl = null;

                    // Skip recording entries as they don't need RTMP URLs
                    if (itm?.isRec === true) {
                        return null;
                    }

                    // Validate that we have a value and title
                    if (
                        !itm?.value
                        || typeof itm.value !== 'string'
                        || itm.value.trim() === ''
                    ) {
                        logger.warn(
                            `Invalid stream value for ${itm?.title}:`,
                            itm?.value
                        );

                        return null;
                    }

                    if (DEFAULT_RTMPS_STATE.hasOwnProperty(itm.title)) {
                        const rtmpLink = DEFAULT_RTMPS_STATE[itm.title];
                        const streamValue = itm.value.trim();

                        // Check if the value is already a complete RTMP/RTMPS URL
                        if (streamValue.match(/^(rtmps?):\/\/.+/)) {
                            rtmpUrl = streamValue;
                        } else if (rtmpLink && streamValue) {
                            // Construct the full URL by combining base URL with stream key
                            rtmpUrl = `${rtmpLink}${streamValue}`;
                        } else {
                            logger.warn(`Unable to construct RTMP URL for ${itm.title}:`, {
                                rtmpLink,
                                streamValue
                            });

                            return null;
                        }
                    } else {
                        logger.warn(`Unknown stream platform: ${itm.title}`);

                        return null;
                    }

                    return rtmpUrl;
                })
                .filter(url => url !== null && url.trim() !== '')
            : [];

        // const streamObject = Object.fromEntries(streamEntries);
        const arrayLength = streamEntries.length;
        const checIFRecordingisOn = stateEnabled.some(it => it.isRec === true);
        const hasLiveStreamingEnabled = arrayLength > 0;

        if (this.state.recordingO || checIFRecordingisOn) {
            if (hasLiveStreamingEnabled) {
                this.props.dispatch(recordingandLivestreamingOn(true));
                this.props._conference.sendCommand(IS_RECORDING_AND_LIVESTREAMING_ON, {
                    value: true
                });
            } else {
                this.props.dispatch(recordingandLivestreamingOn(false));
                this.props._conference.sendCommand(IS_RECORDING_AND_LIVESTREAMING_ON, {
                    value: false
                });
            }

            startRecordingSafely(this.props._conference, {
                broadcastId: selectedBroadcastID,
                mode: Mode,
                appData,
                streamId: arrayLength >= 1 ? streamEntries[0] : null,
                fbstreamId: arrayLength > 1 ? streamEntries[1] : null
            });
        } else {
            // Validate that we have at least one valid stream URL before starting
            if (arrayLength === 0 && !this.state.recordingO) {
                logger.error(
                    'No valid stream URLs found and recording is not enabled'
                );
                this.props.dispatch(
                    showErrorNotification({
                        titleKey: 'liveStreaming.error',
                        descriptionKey: 'liveStreaming.noValidStreams'
                    })
                );

                return false;
            }

            try {
                startRecordingSafely(this.props._conference, {
                    broadcastId: selectedBroadcastID,
                    mode: Mode,
                    streamId: arrayLength >= 1 ? streamEntries[0] : null,
                    fbstreamId: arrayLength > 1 ? streamEntries[1] : null,
                    igStreamid: arrayLength >= 2 ? streamEntries[2] : null
                });

            } catch (error) {
                this.props.dispatch(
                    showErrorNotification({
                        titleKey: 'liveStreaming.error',
                        descriptionKey: 'liveStreaming.startFailed',
                        description: error.message || i18next.t('liveStreaming.unknownError')
                    })
                );

                return false;
            }
        }

        if (_confSettings?.AUTO_CAPTION_ON_RECORDING ?? _autoCaptionOnRecord) {
            if (_confSettings?.TRANSCRIPTION_QUOTA_EXCEEDED === 1) {
                this.props.dispatch(showErrorNotification(
                    {
                        descriptionKey: i18next.t('transcribing.transcriptionQuotaExceeded'),
                        titleKey: i18next.t('transcribing.transcriptionQuotaExceededTitle')
                    }));
            } else if (_requestingSubtitles === true) {
                // no action
            } else {
                this.props.dispatch(toggleRequestingSubtitles());
            }
        }

        return true;
    }

    _onSubmitNative: () => boolean;

    /**
   * Invokes the passed in {@link onSubmit} callback with the entered stream
   * key, and then closes {@code StartLiveStreamDialog}.
   *
   * @private
   * @returns {boolean} False if no stream key is entered to preventing
   * closing, true to close the modal.
   */
    _onSubmitNative() {
        const { broadcasts, selectedBoundStreamID } = this.state;
        const {
            token,
            _oneDriveToken,
            apiN: { API_URL_PREFIX, API_VERSION_PREFIX, MH_CLIENT_ID },
            _accessToken,
            _roomName,
            _confSettings,
            _requestingSubtitles,
            _oneDriveRToken,
            _oneDriveClientId,
            _rToken
        } = this.props;
        const storageType = this.state.recTypeData?.storage;
        const isLegacyCustomStorage = Boolean(storageType)
            && !isLocalRecordingStorage(storageType)
            && storageType !== 'Dropbox'
            && storageType !== 'OneDrive';
        const isCustomS3Recording = isCustomS3RecordingStorage(storageType)
            || isLegacyCustomStorage;
        const hasRecordingRequested = this.state.recordingO
            || (Array.isArray(this.state.streamEnabledMobile)
                && this.state.streamEnabledMobile.some(stream => stream?.isRec === true));

        if (hasRecordingRequested && this._isStorageFullForSelectedRecordingService()) {
            this.props.dispatch(showErrorNotification({
                titleKey: 'recording.storageFullTitle',
                descriptionKey: 'recording.storageFullDescription'
            }));

            return false;
        }

        let appData;
        const attributes = {};
        let selectedBroadcastID = null;

        if (selectedBoundStreamID) {
            const selectedBroadcast
                = broadcasts
                && broadcasts.find(
                    broadcast => broadcast.boundStreamID === selectedBoundStreamID
                );

            selectedBroadcastID = selectedBroadcast && selectedBroadcast.id;
        }

        const streamKeys = { ...this.state.streamKeys };

        const streamMobile = JSON.stringify(this.state.streamEnabledMobile);
        const streamsEnable = { ...this.state.streamsEnabled };
        const streams = {
            streamKeys,
            streamsEnable,
            recording: this.state.recordingO
        };
        const storageAssign = JSON.stringify(streams);

        jitsiLocalStorage.setItem('streamEnabledMobile', streamMobile);
        jitsiLocalStorage.setItem('liveStreamDetails', storageAssign);
        sendAnalytics(createLiveStreamingDialogEvent('start', 'confirm.button'));

        const isDropboxRecording = this.state.selectedRecordingService === RECORDING_TYPES.DROPBOX && token;
        const isOneDriveRecording = this.state.selectedRecordingService === RECORDING_TYPES.ONEDRIVE && _oneDriveToken;
        const isMeetHourStorage = this.state.selectedRecordingService === RECORDING_TYPES.MH_REC_SERVICE;
        const isCloudRecording = isDropboxRecording || isOneDriveRecording || isCustomS3Recording || isMeetHourStorage;

        if (isCloudRecording) {
            const isAwsLikeRecording = isCustomS3Recording || isMeetHourStorage;
            const recordingType = isAwsLikeRecording
                ? storageType
                : isOneDriveRecording
                    ? 'OneDrive'
                    : 'Dropbox';
            const serviceData = isAwsLikeRecording
                ? this.state.recTypeData
                : {
                    token: isOneDriveRecording ? _oneDriveToken : token
                };

            const recordURL = `${API_URL_PREFIX}${API_VERSION_PREFIX}customer/saverecordings`;
            const decryptUrl = `${API_URL_PREFIX}${API_VERSION_PREFIX}customer/recordsettings_decrypt`;

            appData = JSON.stringify({
                file_recording_metadata: {
                    upload_credentials: {
                        // Keep legacy wire value so downstream Jibri/portal parsing remains compatible.
                        service_name: isAwsLikeRecording
                            ? 'awss3'
                            : isOneDriveRecording
                                ? RECORDING_TYPES.ONEDRIVE
                                : RECORDING_TYPES.DROPBOX,
                        recordingType,
                        ...serviceData,
                        userid: this.props._userId?.id,
                        _accessToken,
                        client_id: MH_CLIENT_ID,
                        onedrive_client_id: _oneDriveClientId,
                        r_token: isOneDriveRecording ? _oneDriveRToken : _rToken,
                        api_key: apiKEY,
                        decryptUrl,
                        ...isDomainWeborNative,
                        urlSaved: recordURL,
                        startTiming: new Date().getTime(),

                        meetingid: _roomName
                    }
                }
            });
            attributes.type = isCustomS3Recording
                ? RECORDING_TYPES.CUSTOM_S3
                : isMeetHourStorage
                    ? RECORDING_TYPES.MH_REC_SERVICE
                    : isOneDriveRecording
                        ? RECORDING_TYPES.ONEDRIVE
                        : RECORDING_TYPES.DROPBOX;
        } else {
            appData = JSON.stringify({
                file_recording_metadata: {
                    share: this.state.sharingEnabled
                }
            });
            attributes.type = RECORDING_TYPES.MH_REC_SERVICE;
        }

        const Mode = this.state.recordingO
            ? MHRecordingConstants.mode.FILE
            : MHRecordingConstants.mode.STREAM;

        // const stateKeys = this.state.streamKeys;
        const stateEnabled = this.state.streamEnabledMobile;

        const streamEntries = Array.isArray(stateEnabled)
            ? stateEnabled
                .map(itm => {
                    let rtmpUrl = null;

                    // Skip recording entries as they don't need RTMP URLs
                    if (itm?.isRec === true) {
                        return null;
                    }

                    // Validate that we have a value and title
                    if (
                        !itm?.value
                        || typeof itm.value !== 'string'
                        || itm.value.trim() === ''
                    ) {
                        logger.warn(
                            `Invalid stream value for ${itm?.title}:`,
                            itm?.value
                        );

                        return null;
                    }

                    if (DEFAULT_RTMPS_STATE.hasOwnProperty(itm.title)) {
                        const rtmpLink = DEFAULT_RTMPS_STATE[itm.title];
                        const streamValue = itm.value.trim();

                        // Check if the value is already a complete RTMP/RTMPS URL
                        if (streamValue.match(/^(rtmps?):\/\/.+/)) {
                            rtmpUrl = streamValue;
                        } else if (rtmpLink && streamValue) {
                            // Construct the full URL by combining base URL with stream key
                            rtmpUrl = `${rtmpLink}${streamValue}`;
                        } else {
                            logger.warn(`Unable to construct RTMP URL for ${itm.title}:`, {
                                rtmpLink,
                                streamValue
                            });

                            return null;
                        }
                    } else {
                        logger.warn(`Unknown stream platform: ${itm.title}`);

                        return null;
                    }

                    return rtmpUrl;
                })
                .filter(url => url !== null && url.trim() !== '')
            : [];

        const arrayLength = streamEntries.length;
        const checIFRecordingisOn = stateEnabled.some(it => it.isRec === true);
        const hasLiveStreamingEnabled = arrayLength > 0;

        if (this.state.recordingO || checIFRecordingisOn) {
            // Only claim "recording AND live streaming" when a live stream was
            // actually requested alongside the recording. Flagging it on every
            // recording start (which is what this used to do) makes
            // AbstractRecordingLabel render the live-stream icon for a plain
            // recording and makes recording/middleware play the "live streaming is
            // on" sound on top of the recording sound. This mirrors the web
            // `_onSubmit` path above.
            if (hasLiveStreamingEnabled) {
                this.props.dispatch(recordingandLivestreamingOn(true));
                this.props._conference.sendCommand(IS_RECORDING_AND_LIVESTREAMING_ON, {
                    value: true
                });
            } else {
                this.props.dispatch(recordingandLivestreamingOn(false));
                this.props._conference.sendCommand(IS_RECORDING_AND_LIVESTREAMING_ON, {
                    value: false
                });
            }

            startRecordingSafely(this.props._conference, {
                broadcastId: selectedBroadcastID,
                mode: Mode,
                appData,
                streamId: arrayLength >= 1 ? streamEntries[0] : null,
                fbstreamId: arrayLength > 1 ? streamEntries[1] : null
            });
        } else {
            // Validate that we have at least one valid stream URL before starting
            if (arrayLength === 0 && !this.state.recordingO) {
                logger.error(
                    'No valid stream URLs found and recording is not enabled'
                );
                this.props.dispatch(
                    showErrorNotification({
                        titleKey: 'liveStreaming.error',
                        descriptionKey: 'liveStreaming.noValidStreams'
                    })
                );

                return false;
            }

            try {
                startRecordingSafely(this.props._conference, {
                    broadcastId: selectedBroadcastID,
                    mode: Mode,
                    streamId: arrayLength >= 1 ? streamEntries[0] : null,
                    fbstreamId: arrayLength > 1 ? streamEntries[1] : null,
                    igStreamid: arrayLength >= 2 ? streamEntries[2] : null
                });
            } catch (error) {
                this.props.dispatch(
                    showErrorNotification({
                        titleKey: 'liveStreaming.error',
                        descriptionKey: 'liveStreaming.startFailed',
                        description: error.message || i18next.t('liveStreaming.unknownError')
                    })
                );

                return false;
            }
        }

        if (
            _confSettings?.AUTO_CAPTION_ON_RECORDING
            ?? this.props._autoCaptionOnRecord
        ) {
            if (_confSettings?.TRANSCRIPTION_QUOTA_EXCEEDED === 1) {
                this.props.dispatch(showErrorNotification(
                    {
                        descriptionKey: i18next.t('transcribing.transcriptionQuotaExceeded'),
                        titleKey: i18next.t('transcribing.transcriptionQuotaExceededTitle')
                    }));
            } else if (_requestingSubtitles === true) {
                // no action
            } else {
                this.props.dispatch(toggleRequestingSubtitles());
            }
        }

        return true;
    }
}

/**
 * Maps part of the Redux state to the component's props.
 *
 * @param {Object} state - The Redux state.
 * @returns {{
 *     _conference: Object,
 *     _googleAPIState: number,
 *     _googleProfileEmail: string,
 *     _streamKey: string
 * }}
 */
export function _mapStateToProps(state: Object) {
    const {
        transcription,
        fileRecordingsServiceEnabled = false,
        fileRecordingsServiceSharingEnabled = false,
        dropbox = {},
        onedrive = {}
    } = state['features/base/config'];
    const { LiveStream, Recording } = state['features/recording'];
    const { _requestingSubtitles } = state['features/subtitles'];

    const {
        interfaceConfig: _interfaceConfig,
        API_URL_PREFIX,
        API_VERSION_PREFIX,
        MH_CLIENT_ID
    } = state['features/base/config'];
    const apiN = {
        API_URL_PREFIX,
        API_VERSION_PREFIX,
        MH_CLIENT_ID
    };

    const { isRecordingandLivestreamingOn = false }
        = state['features/base/mtApi'];

    const maxLiveStreamsAllowed = state['features/base/mtApi']?.pregData?.max_live_streams_allowed;


    return {
        _conference: state['features/base/conference'].conference,
        _googleAPIState: state['features/google-api'].googleAPIState,
        _googleProfileEmail: state['features/google-api'].profileEmail,
        _streamKey: state['features/recording'].streamKey,
        _appKey: dropbox.appKey,
        _autoCaptionOnRecord: transcription?.autoCaptionOnRecord ?? false,
        _fileRecordingsServiceEnabled: fileRecordingsServiceEnabled,
        _fileRecordingsServiceSharingEnabled: fileRecordingsServiceSharingEnabled,
        _isDropboxEnabled: isDropboxEnabled(state),
        _isOneDriveEnabled: isOneDriveEnabled(state),
        token: state['features/dropbox'].token,
        _oneDriveToken: state['features/onedrive'].token,
        _liveStream: LiveStream,
        _recording: Recording,
        _roomName: state['features/base/conference']?.room,
        _userId: state['features/base/jwt']?.meetingDetails,
        _interfaceConfig,
        _accessToken: state['features/base/jwt']?.accessToken,
        apiN,
        _maxLiveStreamsAllowed: Number(maxLiveStreamsAllowed) || 2,
        _rToken: state['features/dropbox'].rToken,
        _oneDriveRToken: state['features/onedrive'].rToken,
        _tokenExpireDate: state['features/dropbox'].expireDate,
        _oneDriveTokenExpireDate: state['features/onedrive'].expireDate,
        _oneDriveClientId: onedrive.clientId,
        _oneDriveAuthStrategy: onedrive.authStrategy,
        _oneDriveAuthorizing: Boolean(state['features/onedrive']?.authorizing),
        _isRecordingandLivestreamingOn: isRecordingandLivestreamingOn,
        _confSettings: state['features/base/jwt']?.confSettings,
        _autoLiveStreamKeys: state['features/recording'].autoLiveStreamKeys,
        _requestingSubtitles
    };
}

/**
 * Maps part of the Redux dispatch state to the component's props.
 *
 * @param {Object} state - The Redux state.
 * @returns {{
 *     _conference: Object,
 *     _googleAPIState: number,
 *     _googleProfileEmail: string,
 *     _streamKey: string
 * }}
 */
export function _mapDispatchToProps(dispatch) {
    return {
        dispatch
    };
}
